






























































































































import {
  computed,
  defineComponent,
  provide,
  reactive,
  ref,
  onMounted
} from "@vue/composition-api";
import { UnprocessableEntityError } from "@/api/types";
import { useGlobalState } from "@/helpers/hooks/useGlobalState";
import { getContractInformation, getCompanyLogo } from "@/api/index";
import { useRequest } from "@/helpers/hooks/useRequest";
import { useOfficial, OFFICIAL_STORE_KEY } from "@/helpers/hooks/useOfficial";
import { formKeyLabelList } from "@/helpers/official";

import LoadingView from "@/components/Molecules/LoadingView/index.vue";
import FormTableOfContents, {
  Content
} from "@/components/Molecules/FormTableOfContents/index.vue";
import MessageBar from "@/components/Atoms/MessageBar/index.vue";
import GuideBar from "@/components/Atoms/GuideBar/index.vue";

import BasicInfoForm from "./Organisms/BasicInfoForm/index.vue";
import CharmForm from "./Organisms/CharmForm/index.vue";
import ImageForm from "./Organisms/ImageForm/index.vue";
import MovieForm from "./Organisms/MovieForm/index.vue";
import OfficialSidebar from "./Organisms/OfficialSidebar/index.vue";

import SaveMethodSelectModal from "./Organisms/modals/SaveMethodSelectModal/index.vue";
import PublishCompleteModal from "./Organisms/modals/PublishCompleteModal/index.vue";
import ReadonlyModal from "@/components/Organisms/modals/ReadonlyModal/index.vue";
import ConfirmModal from "@/components/Organisms/modals/ConfirmModal/index.vue";
import { getCompanyId } from "@/helpers/auth-helper";
import { useRouter } from "@/router";
import { useStore } from "@/store";

export default defineComponent({
  components: {
    LoadingView,
    FormTableOfContents,
    MessageBar,
    GuideBar,

    BasicInfoForm,
    CharmForm,
    ImageForm,
    MovieForm,
    OfficialSidebar,

    SaveMethodSelectModal,
    PublishCompleteModal,
    ReadonlyModal,
    ConfirmModal
  },
  name: "Official",
  setup() {
    /** ====================================================
     * ストア
     ==================================================== */
    provide(OFFICIAL_STORE_KEY, useOfficial());
    const {
      officialsData,
      officialDetailData,
      isPendingVersion,
      officialLoaded,
      loadOfficial,
      loadLatestOfficial,
      officialEditingForm,
      mergedErrorMessageList,
      getOfficialErrorMessage,
      runCreateOfficial,
      officialCreateError,
      runUpdateOfficial,
      officialUpdateError,
      runConfirmOfficial,
      officialConfirmError,
      runUnconfirmOfficial,
      officialUnconfirmError,
      apiRequesting
    } = useGlobalState(OFFICIAL_STORE_KEY);

    const isNewOfficial = computed(() => {
      return officialsData.value?.officials?.length === 0;
    });

    const companyLogoUrl = ref<string>();
    const isShowCompanyLogoModal = ref(false);
    const clickedConfirm = ref<boolean>(false);

    onMounted(async () => {
      await checkCompanyLogo();
    });

    const checkCompanyLogo = async () => {
      const response = await getCompanyLogo();
      if (response && response.data && response.data.square_logo_url) {
        companyLogoUrl.value = response.data.square_logo_url;
      }
      if (response?.status === 400 || !response?.data?.square_logo_url) {
        isShowCompanyLogoModal.value = true;
      }
    };

    const companyLogoModalDesc = computed(() => {
      const text = `企業ページを掲載するには企業ロゴが必要です。\n「設定 > 企業ロゴの設定」から設定してください。`;
      const warn = `\n※ 編集中の内容は下書き保存されています。`;
      return clickedConfirm.value ? text + warn : text;
    });

    const companyLogoModalCancelButton = computed(() => {
      return clickedConfirm.value ? "キャンセル" : "後で設定する";
    });

    const closeCompanyLogoModal = () => {
      isShowCompanyLogoModal.value = false;
    };

    const onClickCompanyModalSetting = () => {
      // 掲載依頼をクリックした場合は下書き保存を実行する
      if (clickedConfirm.value) {
        if (
          isNewOfficial.value ||
          officialDetailData.value?.public_status === "public"
        ) {
          runCreateOfficial();
          if (officialCreateError.value) {
            const errorMessage = extractUErrorMessage(
              officialCreateError.value
            );
            showErrorModal(errorMessage);
            return;
          }
        } else {
          runUpdateOfficial();
        }
      }

      router.push({
        name: "AccountsCompanyLogo"
      });
    };

    const onClickConfirm = async () => {
      clickedConfirm.value = true;
      await checkCompanyLogo();
    };

    /** ====================================================
     * データ読み込み
     ==================================================== */
    loadOfficial();

    const [{ data: contractsData, loading: contractLoading }] = useRequest(
      () => getContractInformation({ contract_types: ["job"] }),
      { immediate: true }
    );

    const router = useRouter();
    const store = useStore();

    /** ====================================================
     * 目次・セクション選択状態管理
     ==================================================== */
    type ContentKey = "basicInfoForm" | "charmForm" | "imageForm" | "movieForm";

    const selectedContentKey = ref<ContentKey>("basicInfoForm");

    const onClickSection = (contentKey: ContentKey) => {
      selectedContentKey.value = contentKey;
    };

    const onClickSubSection = ([contentKey, subContentKey]: [
      ContentKey,
      string
    ]) => {
      // TODO: イベント詳細のように、ページ内リンクで移動させる。2023年7月現在は「サブセクションをクリックしたら、該当セクションに飛ぶ」という動作になっているが想定内である
      selectedContentKey.value = contentKey;
      router
        .push({
          name: "OfficialEdit",
          hash: `#${subContentKey}`
        })
        .catch(() => {});
    };

    const tableOfContents = computed(() => {
      if (!officialEditingForm.value) return [];

      const addErrorCount = (
        content: Content<ContentKey>
      ): Content<ContentKey> => ({
        ...content,
        errorCount: content.subContents.filter(
          subContent => subContent?.invalid
        ).length
      });

      const contents: Content<ContentKey>[] = [
        {
          key: "basicInfoForm",
          name: formKeyLabelList.official_data.label,
          subContents: [
            {
              key: "contract",
              name: formKeyLabelList.contract_id.label,
              invalid: !!getOfficialErrorMessage({ mainKey: "contract_id" })
            },
            {
              key: "official_image",
              name: formKeyLabelList.official_image.label,
              invalid: !!getOfficialErrorMessage({
                mainKey: "official_image"
              })
            },
            {
              key: "top_head_image",
              name: formKeyLabelList.top_head_image.label,
              invalid: !!getOfficialErrorMessage({
                mainKey: "top_head_image"
              })
            },
            {
              key: "official_description",
              name: formKeyLabelList.official_description.label,
              invalid: !!getOfficialErrorMessage({
                mainKey: "official_description"
              })
            },
            ...(officialEditingForm.value.official_data
              ? officialEditingForm.value.official_data.map(
                  (data, dataIndex) => {
                    const textError = getOfficialErrorMessage({
                      mainKey: "official_data",
                      id: data.id || data.tmpId,
                      subKey: "text"
                    });
                    const labelError = getOfficialErrorMessage({
                      mainKey: "official_data",
                      id: data.id || data.tmpId,
                      subKey: "label"
                    });
                    const combinedError =
                      textError && labelError
                        ? textError + ", " + labelError
                        : textError || labelError;

                    return {
                      key: `official_data${dataIndex}`,
                      name: data.label,
                      invalid: !!combinedError
                    };
                  }
                )
              : [])
          ]
        },
        {
          key: "charmForm",
          name: formKeyLabelList.charms.section_name.label,
          subContents: [...(officialEditingForm.value.charms || [])].map(
            (charm, index) => {
              return {
                key: `charm${charm.id || charm.tmpId}`,
                name: `${formKeyLabelList.charms.section_name.label}${index +
                  1}`,
                invalid: !!(
                  getOfficialErrorMessage({
                    mainKey: "charms",
                    id: charm.id || charm.tmpId,
                    subKey: "image1"
                  }) ||
                  getOfficialErrorMessage({
                    mainKey: "charms",
                    id: charm.id || charm.tmpId,
                    subKey: "title"
                  }) ||
                  getOfficialErrorMessage({
                    mainKey: "charms",
                    id: charm.id || charm.tmpId,
                    subKey: "description1"
                  })
                )
              };
            }
          )
        },
        {
          key: "imageForm",
          name: formKeyLabelList.images.section_name.label,
          subContents: [...(officialEditingForm.value.images || [])].map(
            (image_gallery, index) => {
              return {
                key: `image_gallery${image_gallery.id || image_gallery.tmpId}`,
                name: `${formKeyLabelList.images.section_name.label}${index +
                  1}`,
                invalid: !!(
                  getOfficialErrorMessage({
                    mainKey: "images",
                    id: image_gallery.id || image_gallery.tmpId,
                    subKey: "image1"
                  }) ||
                  getOfficialErrorMessage({
                    mainKey: "images",
                    id: image_gallery.id || image_gallery.tmpId,
                    subKey: "title"
                  }) ||
                  getOfficialErrorMessage({
                    mainKey: "images",
                    id: image_gallery.id || image_gallery.tmpId,
                    subKey: "description1"
                  })
                )
              };
            }
          )
        },
        {
          key: "movieForm",
          name: formKeyLabelList.movies.section_name.label,
          subContents: [...(officialEditingForm.value.movies || [])].map(
            (movie, index) => {
              return {
                key: `movie${movie.id || movie.tmpId}`,
                name: `${formKeyLabelList.movies.section_name.label}${index +
                  1}`,
                invalid: !!(
                  getOfficialErrorMessage({
                    mainKey: "movies",
                    id: movie.id || movie.tmpId,
                    subKey: "movie1"
                  }) ||
                  getOfficialErrorMessage({
                    mainKey: "movies",
                    id: movie.id || movie.tmpId,
                    subKey: "title"
                  }) ||
                  getOfficialErrorMessage({
                    mainKey: "movies",
                    id: movie.id || movie.tmpId,
                    subKey: "description1"
                  })
                )
              };
            }
          )
        }
      ];

      return contents.map(addErrorCount);
    });

    /** ====================================================
     * モーダル
     ==================================================== */
    const modalShowStatus = reactive({
      // 下書き保存方法 選択モーダル
      saveMethodSelectModal: false,
      // 掲載依頼 完了モーダル
      publishCompleteModal: false,
      // 掲載依頼取り消し 完了モーダル
      unconfirmCompleteModal: false,
      // バージョン編集不可モーダル
      readonlyModal: true
    });

    const showModal = (modalKey: keyof typeof modalShowStatus) => {
      modalShowStatus[modalKey] = true;
    };

    const closeModal = (modalKey: keyof typeof modalShowStatus) => {
      modalShowStatus[modalKey] = false;
    };

    const onClosePublishCompleteModal = () => {
      closeModal("publishCompleteModal");
      loadOfficial();
    };

    const onCloseUnconfirmCompleteModal = () => {
      closeModal("unconfirmCompleteModal");
      loadOfficial();
    };

    /** ====================================================
     * 保存・掲載依頼・プレビュー
     ==================================================== */

    const onSaveDraft = async () => {
      if (isNewOfficial.value) {
        await runCreateOfficial();
        if (officialCreateError.value) {
          const errorMessage = extractUErrorMessage(officialCreateError.value);
          showErrorModal(errorMessage);
          return;
        }
        loadLatestOfficial();
      } else {
        // 公開中のバージョン → 新規バージョンとして保存
        if (officialDetailData.value?.public_status === "public") {
          await runCreateOfficial();
          if (officialCreateError.value) {
            const errorMessage = extractUErrorMessage(
              officialCreateError.value
            );
            showErrorModal(errorMessage);
            return;
          }
          loadLatestOfficial();
        }
        // それ以外のバージョン → 上書き保存 or 新規バージョン保存か選択可
        else {
          showModal("saveMethodSelectModal");
        }
      }
    };

    const publishConfirm = async () => {
      // バージョンを保存
      if (isNewOfficial.value) {
        await runCreateOfficial();
        if (officialCreateError.value) {
          const errorMessage = extractUErrorMessage(officialCreateError.value);
          showErrorModal(errorMessage);
          return;
        } else {
          await loadOfficial();
        }
      } else {
        await runUpdateOfficial();
        if (officialUpdateError.value) {
          const errorMessage = extractUErrorMessage(officialUpdateError.value);
          showErrorModal(errorMessage);
          return;
        }
      }

      // バージョンの掲載依頼
      await runConfirmOfficial();
      if (officialConfirmError.value) {
        // 企業ロゴ未設定の場合
        if (
          officialConfirmError.value instanceof UnprocessableEntityError &&
          officialConfirmError.value?.response.data.errors[0].name ===
            "square_logo"
        ) {
          const route = router.resolve({ name: "AccountsCompanyLogo" }).href;
          store.commit("common/updateAlertView", {
            isVisible: true,
            title: "企業ページの掲載依頼には企業ロゴの設定が必要です。",
            message: `企業ロゴは <a href="${route}" target="_blank" rel="noopener noreferrer" class="link link--external">設定メニュー</a> から設定できます。`,
            bodyType: "html"
          });
        } else {
          if (officialConfirmError.value instanceof UnprocessableEntityError) {
            const messages =
              officialConfirmError.value?.response.data.errors[0].messages;
            store.commit("common/updateAlertView", {
              isVisible: true,
              title: "エラーが発生しました",
              message: messages[0]
            });
          } else {
            showErrorModal();
          }
        }
        return;
      }

      showModal("publishCompleteModal");
    };

    const publishUnconfirm = async () => {
      await runUnconfirmOfficial();
      if (officialUnconfirmError.value) {
        showErrorModal();
        return;
      }
      showModal("unconfirmCompleteModal");
    };

    const selectUpdate = async () => {
      closeModal("saveMethodSelectModal");
      await runUpdateOfficial();
      if (officialUpdateError.value) {
        const errorMessage = extractUErrorMessage(officialUpdateError.value);
        showErrorModal(errorMessage);
        return;
      }
      loadOfficial();
    };

    const selectNewSave = async () => {
      closeModal("saveMethodSelectModal");
      await runCreateOfficial();
      if (officialCreateError.value) {
        const errorMessage = extractUErrorMessage(officialCreateError.value);
        showErrorModal(errorMessage);
        return;
      }

      loadLatestOfficial();
    };

    const preview = async () => {
      if (isNewOfficial.value) {
        await runCreateOfficial();
        if (officialCreateError.value) {
          const errorMessage = extractUErrorMessage(officialCreateError.value);
          showErrorModal(errorMessage);
          return;
        }

        loadLatestOfficial();
      } else if (
        officialDetailData.value?.approval_status === "draft" ||
        officialDetailData.value?.approval_status === "rejected"
      ) {
        // 承認ステータスが下書き・棄却の場合は入力内容を下書き保存してからプレビューを表示する
        // c.f. https://github.com/OneCareerJP/cloud/blob/533c497506b677d5be577a74391b6de78cb6da89/rms/app/models/official.rb#L33-L35
        await runUpdateOfficial();
        if (officialUpdateError.value) {
          const errorMessage = extractUErrorMessage(officialUpdateError.value);
          showErrorModal(errorMessage);
          return;
        }
      }
      if (officialDetailData.value?.id) {
        const companyId = getCompanyId();
        const previewUrl = `${process.env["VUE_APP_ONECAREER_URL"]}/companies/${companyId}?official_id=${officialDetailData.value?.id}&show_all_recruits=true`;
        window.open(previewUrl, "_blank", "noopener");
      }
    };

    const onUnconfirm = () => {
      modalShowStatus.readonlyModal = false;
      publishUnconfirm();
    };

    const showErrorModal = (errorMessage?: string) => {
      if (errorMessage) {
        store.commit("common/updateAlertView", {
          isVisible: true,
          title: "エラーが発生しました",
          message: errorMessage
        });
      } else {
        store.commit("common/updateAlertView", {
          isVisible: true,
          title: "予期せぬエラーが発生しました",
          message: "再読み込みを実行してください"
        });
      }
    };

    const extractUErrorMessage = (error: any) => {
      // 422以外の場合は表示すべきエラーメッセージはないためundefinedを返却する
      if (!error || !(error instanceof UnprocessableEntityError)) {
        return undefined;
      }

      const errorMessages = error.response.data.errors;
      const errorMessagesList = errorMessages.flatMap(
        (error: { messages: string[] }) => error.messages
      );

      // エラーモーダルではメッセージを一つしか表示できないため、バックエンドから返却された最初のエラーメッセージを表示する
      return errorMessagesList[0];
    };

    /** ====================================================
     * 画面表示用ロジック
     ==================================================== */
    const rejectReasonLines = computed<number>(() => {
      if (
        !officialDetailData.value ||
        !officialDetailData.value.reject_reason
      ) {
        return 0;
      }

      const newLineCodeCount = officialDetailData.value.reject_reason.match(
        /\r\n|\r|\n/g
      )?.length;
      return newLineCodeCount ? newLineCodeCount + 1 : 0;
    });

    return {
      officialDetailData,
      isPendingVersion,
      officialLoaded,
      mergedErrorMessageList,
      apiRequesting,

      contractsData,
      contractLoading,

      isNewOfficial,
      selectedContentKey,
      onClickSection,
      onClickSubSection,
      tableOfContents,
      isShowCompanyLogoModal,
      companyLogoUrl,
      companyLogoModalDesc,
      companyLogoModalCancelButton,
      closeCompanyLogoModal,
      onClickConfirm,
      onClickCompanyModalSetting,

      onClosePublishCompleteModal,
      onCloseUnconfirmCompleteModal,

      modalShowStatus,
      showModal,
      closeModal,

      onSaveDraft,
      publishConfirm,
      publishUnconfirm,
      selectUpdate,
      selectNewSave,
      preview,
      onUnconfirm,

      rejectReasonLines
    };
  }
});
